import { useId, useRef, useEffect, useState } from 'react'
import { Props_SnippetTimeline } from './Timeline.props'
import { Legacy_CompileMarkdown } from '@ClientBase/React/ReactSnippetMDCompiler'

const SnippetTimeline = (props: Props_SnippetTimeline) => {
    const { heading, repeatable_entry } = props
    const snippetId = useId()
    const classHeadingVisibility: string = heading.visibility.value ? `snippet-timeline__heading--${heading.visibility.value}` : ''
    const cleanupRef = useRef<(() => void) | null>(null)
    const isDownRef = useRef<boolean>(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const [currentIndex, setCurrentIndex] = useState<number>(0)

    const scrollToEntry = (index: number) => {
        if (!containerRef.current) return

        const container = containerRef.current
        const entries = container.querySelectorAll('.snippet-timeline__entry-container')
        const targetEntry = entries[index] as HTMLElement

        if (targetEntry) {
            const containerRect = container.getBoundingClientRect()
            const entryRect = targetEntry.getBoundingClientRect()
            const containerCenter = containerRect.width / 2
            const entryCenter = entryRect.width / 2
            const scrollOffset = entryRect.left - containerRect.left - containerCenter + entryCenter

            container.scrollBy({
                left: scrollOffset,
                behavior: 'smooth'
            })

            setCurrentIndex(index)
        }
    }

    const handlePrevious = () => {
        const newIndex = Math.max(0, currentIndex - 1)
        scrollToEntry(newIndex)
    }

    const handleNext = () => {
        const newIndex = Math.min(repeatable_entry.length - 1, currentIndex + 1)
        scrollToEntry(newIndex)
    }

    const updateCurrentEntry = () => {
        if (!containerRef.current) return

        const container = containerRef.current
        const entries = container.querySelectorAll('.snippet-timeline__entry-container')
        const windowCenterX = window.innerWidth / 2

        let closestIndex = 0
        let closestDistance = Infinity

        entries.forEach((entry, index) => {
            const rect = entry.getBoundingClientRect()
            const entryCenterX = rect.left + rect.width / 2
            const distance = Math.abs(entryCenterX - windowCenterX)

            if (distance < closestDistance) {
                closestDistance = distance
                closestIndex = index
            }
        })

        setCurrentIndex(closestIndex)
    }

    useEffect(() => {
        updateCurrentEntry()

        const handleScrollOrResize = () => {
            updateCurrentEntry()
        }

        window.addEventListener('scroll', handleScrollOrResize, true)
        window.addEventListener('resize', handleScrollOrResize)

        return () => {
            if (cleanupRef.current) cleanupRef.current()
            window.removeEventListener('scroll', handleScrollOrResize, true)
            window.removeEventListener('resize', handleScrollOrResize)
        }
    }, [])

    const handleScroll = (event: React.PointerEvent<HTMLDivElement>) => {
        if (cleanupRef.current) cleanupRef.current()
        const container = event.currentTarget
        isDownRef.current = true
        const startX = event.pageX - container.offsetLeft
        const scrollLeft = container.scrollLeft

        const onPointerMove = (e: PointerEvent) => {
            if (!isDownRef.current) return
            e.preventDefault()
            const x = e.pageX - container.offsetLeft
            const walk = (x - startX) * 2
            container.scrollLeft = scrollLeft - walk
            updateCurrentEntry()
        }

        const onPointerUp = () => {
            isDownRef.current = false
            container.removeEventListener('pointermove', onPointerMove)
            container.removeEventListener('pointerup', onPointerUp)
            container.removeEventListener('pointerleave', onPointerUp)
        }

        container.addEventListener('pointermove', onPointerMove)
        container.addEventListener('pointerup', onPointerUp)
        container.addEventListener('pointerleave', onPointerUp)

        cleanupRef.current = () => {
            container.removeEventListener('pointermove', onPointerMove)
            container.removeEventListener('pointerup', onPointerUp)
            container.removeEventListener('pointerleave', onPointerUp)
        }
    }

    return (
    <section className="snippet-timeline">
        <div className="snippet-timeline__container">
            <h2 className={`snippet-timeline__heading ${classHeadingVisibility}`}>{ heading.content.value }</h2>
            <div className="snippet-timeline__container-entries-wrapper">
                <div className="snippet-timeline__container-entries" ref={containerRef} onPointerDown={handleScroll} >
                    { repeatable_entry.map((entry, index: number) => (
                        <div key={`snippet-timeline-${snippetId}-${index}`} className={`snippet-timeline__entry-container ${index === currentIndex ? 'snippet-timeline__entry-container--current' : ''}`}>
                            <button 
                                className="snippet-timeline__entry-id" 
                                onClick={() => scrollToEntry(index)}
                                aria-label={`{{js.snippet.timeline.entries.slide.button.id.aria-label}} ${index + 1}`}
                            >
                                { index + 1 }
                            </button>
                            <div className="snippet-timeline__entry-horizontal-line"></div>
                            <div className={`snippet-timeline__entry-vertical-line`}></div>
                            <div className="snippet-timeline__entry">
                                { entry.content?.value && <div className="snippet-timeline__entry-content">{ Legacy_CompileMarkdown(entry.content.value) }</div> }
                            </div>
                        </div>
                    ))}
                </div>
                <nav className="snippet-timeline__container-navigation">
                    <button 
                        className="snippet-timeline__navigation-button snippet-timeline__navigation-button--previous" 
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                        aria-label="{{js.snippet.timeline.entries.navigation.button.previous.aria-label}}"
                    >
                    </button>
                    <button 
                        className="snippet-timeline__navigation-button snippet-timeline__navigation-button--next" 
                        onClick={handleNext}
                        disabled={currentIndex === repeatable_entry.length - 1}
                        aria-label="{{js.snippet.timeline.entries.navigation.button.next.aria-label}}"
                    >
                    </button>
                </nav>
                <nav className="snippet-timeline__container-pagination">
                    { repeatable_entry.map((_, index: number) => (
                        <button 
                            key={`snippet-timeline-pagination-${snippetId}-${index}`} 
                            className={`snippet-timeline__pagination-bullet ${index === currentIndex ? 'snippet-timeline__pagination-bullet--active' : ''}`} 
                            onClick={() => scrollToEntry(index)}
                            aria-label={`{{js.snippet.timeline.entries.pagination.bullet.aria-label}} ${index + 1}`}
                        >
                        </button>
                    ))}
                </nav>
            </div>
        </div>
    </section>
    )
}

export default SnippetTimeline